// Enterprise Warranty Management Portal - Tamil Heritage Design
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Warranty } from '../types/warranty';
import { 
  WarrantySearchFilters,
  ShareModal 
} from '../components/warranty';
import { useWarrantyFilters, useShareModal } from '../hooks/useWarranty';
import { warrantyService } from '../services/warranty';
import '../styles/components/EnterpriseWarrantyPortal.css';

// Cache busting utility for icons
const getCacheBustedIconUrl = (iconName: string) => {
  if (import.meta.env.DEV) {
    const timestamp = Date.now();
    return `/icons/${iconName}.svg?v=${timestamp}`;
  }
  return `/icons/${iconName}.svg`;
};

// Icon component
const Icon = ({ name, className = "" }: { name: string; className?: string }) => {
  return (
    <img 
      src={getCacheBustedIconUrl(name)} 
      alt="" 
      className={`icon ${className}`}
      onError={(e) => {
        console.error(`Failed to load icon: ${name}.svg`);
        e.currentTarget.style.display = 'none';
      }}
    />
  );
};

const WarrantyListPage: React.FC = () => {
  // State for API data
  const [warranties, setWarranties] = useState<Warranty[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showRightPanel, setShowRightPanel] = useState(false);

  // Hooks
  const navigate = useNavigate();

  // Custom hooks for state management
  const {
    filters,
    filteredWarranties,
    stats,
    categories,
    providers,
    showAdvancedFilters,
    updateFilters,
    clearFilters,
    toggleAdvancedFilters
  } = useWarrantyFilters(warranties);

  const { shareModal, openShareModal, closeShareModal } = useShareModal();

  // Load warranties from API
  useEffect(() => {
    loadWarranties();
  }, []);

  const loadWarranties = async () => {
    try {
      setLoading(true);
      setError(null);
      const userEmail = localStorage.getItem('userEmail') || 'test@test.com';
      const warrantyData = await warrantyService.getUserWarranties(userEmail);
      // Convert WarrantyResponse[] to Warranty[]
      const convertedWarranties = warrantyData.map(w => ({
        id: String(w.id || Math.random()),
        userId: w.userId,
        productName: w.productName || 'Unknown Product',
        brand: w.brand || 'Unknown Brand',
        modelNumber: w.modelNumber || 'N/A',
        purchaseDate: w.purchaseDate || '',
        purchaseStore: w.purchaseStore,
        warrantyExpiryDate: w.warrantyExpiryDate || '',
        status: (w.status || 'active') as 'active' | 'expiring' | 'expired',
        warrantyProvider: w.warrantyProvider || 'Unknown Provider',
        category: (w.category || 'other') as 'electronics' | 'appliances' | 'automotive' | 'home' | 'other',
        tags: typeof w.tags === 'string' ? w.tags.split(',').map(t => t.trim()).filter(Boolean) : (w.tags || []),
        entryMethod: (w.entryMethod || 'manual') as 'manual' | 'document-extraction' | 'auto-import',
        createdAt: w.createdAt,
        updatedAt: w.updatedAt,
        documentId: w.documentId
      }));
      setWarranties(convertedWarranties);
    } catch (err) {
      console.error('Failed to load warranties:', err);
      setError('Failed to load warranties. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Event handlers
  const handleClearFilters = () => {
    clearFilters();
  };

  const handleView = (warranty: Warranty) => {
    console.log('View warranty:', warranty);
    // TODO: Navigate to warranty detail page
  };

  const handleAddNew = () => {
    navigate('/warranty/intelligence');
  };

  const handleBulkImport = () => {
    console.log('Bulk import warranties');
    // TODO: Open bulk import modal
  };

  const handleGenerateReport = () => {
    console.log('Generate report');
    // TODO: Generate PDF/Excel report
  };

  return (
    <div className="enterprise-warranty-portal">
      {/* Hero Header with Tamil Heritage Design */}
      <div className="portal-hero-header">
        <div className="hero-content">
          <div className="hero-title-section">
            <div className="hero-icon-stack">
              <Icon name="warranty-shield" className="hero-primary-icon" />
              <div className="hero-icon-sparkles">
                <Icon name="star" className="sparkle-icon sparkle-1" />
                <Icon name="star" className="sparkle-icon sparkle-2" />
                <Icon name="star" className="sparkle-icon sparkle-3" />
              </div>
            </div>
            <div className="hero-text">
              <h1 className="hero-title">
                <span className="title-primary">Warranty</span>
                <span className="title-accent">சுவடி</span>
                <span className="title-secondary">Portal</span>
              </h1>
              <p className="hero-subtitle">Enterprise Asset Protection & Heritage Documentation</p>
            </div>
          </div>
          
          {/* Live Metrics Dashboard */}
          <div className="hero-metrics-grid">
            <div className="metric-card metric-total">
              <Icon name="storage" className="metric-icon" />
              <div className="metric-content">
                <div className="metric-number">{stats.total}</div>
                <div className="metric-label">Total Assets</div>
              </div>
            </div>
            <div className="metric-card metric-active">
              <Icon name="warranty-shield" className="metric-icon" />
              <div className="metric-content">
                <div className="metric-number">{stats.active}</div>
                <div className="metric-label">Protected</div>
              </div>
            </div>
            <div className="metric-card metric-expiring">
              <Icon name="expiring" className="metric-icon" />
              <div className="metric-content">
                <div className="metric-number">{stats.expiring}</div>
                <div className="metric-label">Action Needed</div>
              </div>
            </div>
            <div className="metric-card metric-actions">
              <button className="action-btn primary" onClick={handleAddNew}>
                <Icon name="add-item" />
                🤖 AI Assistant
              </button>
              <button className="action-btn secondary" onClick={handleBulkImport}>
                <Icon name="smart-analytics" />
                Bulk Import
              </button>
            </div>
          </div>
        </div>
        
        {/* Decorative Tamil Pattern */}
        <div className="hero-pattern-overlay">
          <div className="pattern-line pattern-1"></div>
          <div className="pattern-line pattern-2"></div>
          <div className="pattern-line pattern-3"></div>
        </div>
      </div>

      {/* Smart Search Command Center */}
      <div className="search-command-center">
        <WarrantySearchFilters
          filters={filters}
          stats={stats}
          onFiltersChange={updateFilters}
          onClearFilters={handleClearFilters}
          categories={categories}
          providers={providers}
          showAdvancedFilters={showAdvancedFilters}
          onToggleAdvancedFilters={toggleAdvancedFilters}
        />
      </div>

      {/* Full Width Main Portal Layout */}
      <div className="portal-main-layout-fullwidth">
        {/* Portfolio Overview */}
        <div className="portfolio-overview">
          <div className="overview-section">
            <h2 className="section-title">
              <Icon name="trending-up" className="section-icon" />
              Portfolio Overview
            </h2>
            <div className="overview-stats">
              <div className="overview-stat">
                <div className="stat-trend positive">
                  <Icon name="arrow-up" className="trend-icon" />
                  +12.5%
                </div>
                <div className="stat-value">₹2.4M</div>
                <div className="stat-label">Total Asset Value</div>
              </div>
              <div className="overview-stat">
                <div className="stat-trend positive">
                  <Icon name="arrow-up" className="trend-icon" />
                  +8.3%
                </div>
                <div className="stat-value">98.2%</div>
                <div className="stat-label">Coverage Rate</div>
              </div>
              <div className="overview-stat">
                <div className="stat-trend positive">
                  <Icon name="arrow-up" className="trend-icon" />
                  +5.7%
                </div>
                <div className="stat-value">45 days</div>
                <div className="stat-label">Avg. Response Time</div>
              </div>
            </div>
          </div>
        </div>

        {/* Premium Warranty Cards Grid */}
        <div className="premium-warranty-grid">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner-enterprise">
                <Icon name="loader" className="spinner-icon" />
              </div>
              <p className="loading-text">Loading your warranty portfolio...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <Icon name="alert-circle" className="error-icon" />
              <h3>Unable to Load Portfolio</h3>
              <p>We're having trouble connecting to your warranty data. Please try again.</p>
              <button className="retry-btn" onClick={loadWarranties}>
                <Icon name="refresh-cw" />
                Retry Loading
              </button>
            </div>
          ) : (
            <div className="warranty-cards-container">
              {filteredWarranties.map((warranty) => (
                <div key={warranty.id} className="premium-warranty-card" onClick={() => handleView(warranty)}>
                  {/* Card Header with Product Image and Status */}
                  <div className="card-header">
                    <div className="product-image-container">
                      <div className="product-placeholder">
                        <Icon name="package" className="placeholder-icon" />
                      </div>
                      <div className={`status-badge ${warranty.status.toLowerCase()}`}>
                        <Icon name={warranty.status === 'active' ? 'check-circle' : warranty.status === 'expired' ? 'x-circle' : 'clock'} className="status-icon" />
                        {warranty.status.charAt(0).toUpperCase() + warranty.status.slice(1)}
                      </div>
                    </div>
                    <div className="card-actions">
                      <button className="action-btn-card" onClick={(e) => { e.stopPropagation(); openShareModal(warranty); }}>
                        <Icon name="share" />
                      </button>
                      <button className="action-btn-card">
                        <Icon name="more-vertical" />
                      </button>
                    </div>
                  </div>

                  {/* Card Content */}
                  <div className="card-content">
                    <div className="product-info">
                      <h3 className="product-name">{warranty.productName}</h3>
                      <div className="product-details">
                        <span className="brand">{warranty.brand}</span>
                        <span className="separator">•</span>
                        <span className="category">{warranty.category}</span>
                      </div>
                    </div>

                    {/* Key Metrics Row */}
                    <div className="card-metrics">
                      <div className="metric-item">
                        <Icon name="calendar" className="metric-icon" />
                        <div className="metric-content">
                          <span className="metric-label">Expires</span>
                          <span className="metric-value">{new Date(warranty.warrantyExpiryDate).toLocaleDateString('en-IN')}</span>
                        </div>
                      </div>
                      <div className="metric-item">
                        <Icon name="shield" className="metric-icon" />
                        <div className="metric-content">
                          <span className="metric-label">Coverage</span>
                          <span className="metric-value">{warranty.warrantyProvider}</span>
                        </div>
                      </div>
                    </div>

                    {/* Entry Method and Location */}
                    <div className="card-metadata">
                      <div className="metadata-item">
                        <Icon name={warranty.entryMethod === 'document-extraction' ? 'scan' : warranty.entryMethod === 'auto-import' ? 'file-text' : 'edit'} className="metadata-icon" />
                        <span className="metadata-text">
                          {warranty.entryMethod === 'document-extraction' ? 'Document Scan' : 
                           warranty.entryMethod === 'auto-import' ? 'Auto Import' : 'Manual Entry'}
                        </span>
                      </div>
                      {warranty.purchaseStore && (
                        <div className="metadata-item">
                          <Icon name="map-pin" className="metadata-icon" />
                          <span className="metadata-text">{warranty.purchaseStore}</span>
                        </div>
                      )}
                    </div>

                    {/* Progress Bar for Time Remaining */}
                    <div className="warranty-progress">
                      <div className="progress-info">
                        <span className="progress-label">Time Remaining</span>
                        <span className="progress-percentage">{Math.max(0, Math.round(((new Date(warranty.warrantyExpiryDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24 * 365)) * 100))}%</span>
                      </div>
                      <div className="progress-bar">
                        <div 
                          className="progress-fill" 
                          style={{ 
                            width: `${Math.max(0, Math.round(((new Date(warranty.warrantyExpiryDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24 * 365)) * 100))}%`,
                            backgroundColor: warranty.status === 'active' ? '#4caf50' : warranty.status === 'expired' ? '#f44336' : '#ff9800'
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>

                  {/* Tamil Heritage Pattern Decoration */}
                  <div className="card-pattern-decoration">
                    <div className="pattern-line pattern-1"></div>
                    <div className="pattern-line pattern-2"></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Collapsible Right Navigation Panel */}
      <div className={`right-nav-panel ${showRightPanel ? 'panel-open' : 'panel-closed'}`}>
        <button 
          className="panel-toggle-btn"
          onClick={() => setShowRightPanel(!showRightPanel)}
        >
          <Icon name={showRightPanel ? 'x' : 'help-circle'} />
          {showRightPanel ? 'Close' : 'Help & Tips'}
        </button>
        
        {showRightPanel && (
          <div className="panel-content">
            {/* Quick Actions */}
            <div className="panel-section">
              <h3 className="panel-section-title">
                <Icon name="zap" className="section-icon" />
                Quick Actions
              </h3>
              <div className="quick-actions">
                <button className="quick-action-btn" onClick={handleAddNew}>
                  <Icon name="plus" />
                  🤖 AI Warranty Assistant
                </button>
                <button className="quick-action-btn" onClick={handleGenerateReport}>
                  <Icon name="download" />
                  Export Portfolio
                </button>
                <button className="quick-action-btn">
                  <Icon name="bell" />
                  Set Reminders
                </button>
              </div>
            </div>

            {/* Tips & Insights */}
            <div className="panel-section">
              <h3 className="panel-section-title">
                <Icon name="lightbulb" className="section-icon" />
                Smart Tips
              </h3>
              <div className="tips-container">
                <div className="tip-card">
                  <Icon name="calendar" className="tip-icon" />
                  <div className="tip-content">
                    <h4>Renewal Reminder</h4>
                    <p>3 warranties expire in the next 30 days. Consider renewal or replacement.</p>
                  </div>
                </div>
                <div className="tip-card">
                  <Icon name="trending-up" className="tip-icon" />
                  <div className="tip-content">
                    <h4>Portfolio Health</h4>
                    <p>Your portfolio is 98.2% protected. Excellent coverage!</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Page Shortcuts */}
            <div className="panel-section">
              <h3 className="panel-section-title">
                <Icon name="navigation" className="section-icon" />
                Navigation
              </h3>
              <div className="navigation-shortcuts">
                <button className="nav-shortcut" onClick={() => window.location.href = '/'}>
                  <Icon name="home" />
                  Dashboard
                </button>
                <button className="nav-shortcut">
                  <Icon name="bar-chart" />
                  Analytics
                </button>
                <button className="nav-shortcut">
                  <Icon name="settings" />
                  Settings
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Share Modal */}
      <ShareModal
        shareModal={shareModal}
        onClose={closeShareModal}
      />

      {/* Floating AI Assistant Button (Mobile) */}
      <button 
        className="floating-ai-button"
        onClick={handleAddNew}
        title="AI Warranty Assistant"
      >
        <Icon name="bot" />
      </button>
    </div>
  );
};

export default WarrantyListPage;
